import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import IconX from '@pano-project/assets/icons/IconX';
import Modal from '../Modal/Modal';
import { useBottomSheetDrag } from '../../hooks/useBottomSheetDrag';
import './_BottomSheetModal.sass';

export interface BottomSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  onGrabberClick?: () => void;
  closeIcon?: boolean;
  onCloseIcon?: () => void;
  disableBackdropClose?: boolean;
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  bodyMaxHeight?: string;
  className?: string;
  headerContent?: ReactNode;
  backdropEnabled?: boolean;
  disableOpenAnimation?: boolean;
}

const BottomSheetModal: React.FC<BottomSheetModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  onGrabberClick,
  closeIcon = true,
  onCloseIcon,
  disableBackdropClose = false,
  height,
  minHeight,
  maxHeight,
  bodyMaxHeight,
  className = '',
  headerContent,
  backdropEnabled = true,
  disableOpenAnimation = false,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const openRafRef = useRef<number | null>(null);

  const triggerClose = useCallback(
    (callback?: () => void) => {
      if (!isClosing) {
        setIsClosing(true);
        setIsVisible(false);
        closeTimerRef.current = setTimeout(() => {
          setShouldRender(false);
          setIsClosing(false);
          if (callback) callback();
          else onClose();
        }, 150);
      }
    },
    [isClosing, onClose],
  );

  const handleClose = useCallback(() => {
    triggerClose();
  }, [triggerClose]);

  // PRX-23109: `disableBackdropClose` already stops
  // the backdrop click and (via the inner Modal it's forwarded to) that
  // click alone from closing the sheet, but drag-to-dismiss was never
  // gated by it at all -- a caller asking for a non-dismissible sheet
  // (WithdrawalWalletTemplatesModal.tsx, while a submission is in flight)
  // could still have it dragged closed. No current caller passes
  // `disableBackdropClose` on a BottomSheetModal, so this changes nothing
  // for any existing consumer.
  const { setContentRef, isDragging } = useBottomSheetDrag({
    onClose: handleClose,
    enabled: isOpen && !isClosing && !disableOpenAnimation && !disableBackdropClose,
  });

  useEffect(() => {
    if (isOpen) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setShouldRender(true);
      setIsClosing(false);
      openRafRef.current = requestAnimationFrame(() => {
        openRafRef.current = requestAnimationFrame(() => setIsVisible(true));
      });
    } else if (shouldRender && !isClosing) {
      setIsClosing(true);
      setIsVisible(false);
      closeTimerRef.current = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 150);
    }
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (openRafRef.current) {
        cancelAnimationFrame(openRafRef.current);
        openRafRef.current = null;
      }
    };
  }, [isOpen]);

  if (!shouldRender) return null;

  const style: React.CSSProperties = {};
  if (height) style.height = height;
  if (minHeight) style.minHeight = minHeight;
  if (maxHeight) style.maxHeight = maxHeight;

  const bodyStyle: React.CSSProperties = {};
  if (bodyMaxHeight) bodyStyle.maxHeight = bodyMaxHeight;

  // PRX-23109: a plain (non-drag) click/tap on the
  // grabber closed the sheet unconditionally -- unlike the backdrop click
  // above, this was never gated by `disableBackdropClose` at all. Same
  // reasoning as the drag-enabled fix above: no current caller passes
  // `disableBackdropClose` on a BottomSheetModal, so this changes nothing
  // for any existing consumer.
  const handleGrabberClick = disableBackdropClose
    ? undefined
    : onGrabberClick
      ? () => triggerClose(onGrabberClick)
      : handleClose;
  const handleCloseClick = onCloseIcon ? () => triggerClose(onCloseIcon) : handleClose;

  return (
    <div
      className={`bottom-sheet-modal ${isVisible ? 'bottom-sheet-modal--open' : ''} ${isClosing ? 'bottom-sheet-modal--closing' : ''} ${isDragging ? 'bottom-sheet-modal--dragging' : ''} ${disableOpenAnimation ? 'bottom-sheet-modal--no-open-animation' : ''} ${className}`}
    >
      <Modal
        backdropEnabled={backdropEnabled}
        onClose={handleClose}
        closeIcon={false}
        fixedPosition
        disableBackdropClose={disableBackdropClose || disableOpenAnimation}
        onCloseIcon={onCloseIcon ? () => triggerClose(onCloseIcon) : undefined}
      >
        <div ref={setContentRef} className="bottom-sheet-modal__content" style={style}>
          {!disableOpenAnimation && (
            <div
              className="bottom-sheet-modal__grabber"
              onClick={handleGrabberClick}
              style={{
                cursor:
                  !disableBackdropClose && (onGrabberClick || onClose) ? 'pointer' : 'default',
                touchAction: 'none',
              }}
            />
          )}

          {title || headerContent || (closeIcon && !disableOpenAnimation) ? (
            <div className="bottom-sheet-modal__header" style={{ touchAction: 'none' }}>
              {headerContent || (title && <h3 className="bottom-sheet-modal__title">{title}</h3>)}
              {closeIcon && !disableOpenAnimation && (
                <button
                  type="button"
                  onClick={handleCloseClick}
                  className="bottom-sheet-modal__close"
                  aria-label="Close"
                >
                  <IconX />
                </button>
              )}
            </div>
          ) : null}

          <div className="bottom-sheet-modal__body" style={bodyStyle}>
            {children}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BottomSheetModal;
